if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dotsContainer: this.querySelector('.slider-dots')
        };
        this.currentDotIndex = 0;
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        setTimeout(() => {
          this.initializeSliderDots();
        }, 100);

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('touchstart', (e) => this.handleTouchStart(e), false);
          this.elements.viewer.addEventListener('touchmove', (e) => this.handleTouchMove(e), false);
          this.elements.viewer.addEventListener('scrollend', this.handleScrollEnd.bind(this));
          this.elements.viewer.addEventListener('scroll', this.debounceScrollEnd.bind(this));
          this.elements.viewer.addEventListener('scroll', () => {
            if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(() => {
              if (window.innerWidth < 750) {
                const slider = this.elements.viewer;
                const items = slider.querySelectorAll('.product__media-item');
                const sliderRect = slider.getBoundingClientRect();
                
                let closestItem = null;
                let minDistance = Infinity;
                
                items.forEach(item => {
                  const itemRect = item.getBoundingClientRect();
                  const distance = Math.abs(itemRect.left - sliderRect.left);
                  
                  if (distance < minDistance) {
                    minDistance = distance;
                    closestItem = item;
                  }
                });
                
                if (closestItem) {
                  slider.scrollTo({
                    left: closestItem.offsetLeft,
                    behavior: 'smooth'
                  });
                }
              }
            }, 50);
          });
          this.elements.viewer.addEventListener('touchend', () => {
            setTimeout(() => {
              this.alignToLeftMargin();
            }, 150);
          });
        }

        this.setupVisibilityObserver();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        if (this.elements.dotsContainer) {
          const visibleMediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item:not([style*="display: none"])'));
          const currentIndex = visibleMediaItems.findIndex(item => 
            item.dataset.mediaId === event.detail.currentElement.dataset.mediaId
          );
          
          if (currentIndex !== -1) {
            const dots = this.elements.dotsContainer.querySelectorAll('.slider-dot');
            dots.forEach((dot, index) => {
              dot.classList.toggle('slider-dot--active', index === currentIndex);
            });
            this.currentDotIndex = currentIndex;
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        
        if (activeMedia) {
          activeMedia.classList.add('is-active');

          if (prepend) {
            this.elements.viewer.scrollTo({
              left: 0,
              behavior: 'smooth'
            });
          }

          const allMediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
          const currentIndex = Array.from(allMediaItems).findIndex(item => item.dataset.mediaId === mediaId);
          this.updateActiveDot(currentIndex);

          if (prepend && activeMedia.parentElement) {
            activeMedia.parentElement.prepend(activeMedia);
            
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              if (activeThumbnail && activeThumbnail.parentElement) {
                activeThumbnail.parentElement.prepend(activeThumbnail);
              }
            }
            
            setTimeout(() => {
              this.initializeSliderDots();
              this.setupVisibilityObserver();
            }, 100);
          }

          this.preventStickyHeader();
          window.setTimeout(() => {
            if (this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
              activeMedia.scrollIntoView({ behavior: 'smooth' });
            }
          });
          this.playActiveMedia(activeMedia);

          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initializeSliderDots() {
        if (!this.elements.dotsContainer) return;
        
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item:not([style*="display: none"])'));
        if (!mediaItems.length) return;

        this.elements.dotsContainer.innerHTML = '';

        mediaItems.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.className = `slider-dot ${index === 0 ? 'slider-dot--active' : ''}`;
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.innerHTML = '<span class="dot-slider"></span>';
          
          dot.addEventListener('click', () => {
            const targetSlide = mediaItems[index];
            if (targetSlide) {
              targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
              const dots = this.elements.dotsContainer.querySelectorAll('.slider-dot');
              dots.forEach((d, i) => {
                d.classList.toggle('slider-dot--active', i === index);
              });
              this.currentDotIndex = index;
            }
          });
          
          this.elements.dotsContainer.appendChild(dot);
        });
      }

      handleTouchStart(e) {
        this.xDown = e.touches[0].clientX;
      }

      handleTouchMove(e) {
        if (!this.xDown) return;

        const xUp = e.touches[0].clientX;
        const xDiff = this.xDown - xUp;

        if (Math.abs(xDiff) > 50) {
          const dots = this.elements.dotsContainer.querySelectorAll('.slider-dot');
          if (xDiff > 0) {
            this.currentDotIndex = Math.min(this.currentDotIndex + 1, dots.length - 1);
          } else {
            this.currentDotIndex = Math.max(this.currentDotIndex - 1, 0);
          }
          this.updateActiveDot(this.currentDotIndex);
        }

        this.xDown = null;
      }

      setupVisibilityObserver() {
        if (!this.elements.viewer) return;

        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const visibleSlide = entry.target;
              const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item:not([style*="display: none"])'));
              const visibleIndex = slides.indexOf(visibleSlide);
              if (visibleIndex !== -1) {
                const dots = this.elements.dotsContainer.querySelectorAll('.slider-dot');
                dots.forEach((dot, index) => {
                  dot.classList.toggle('slider-dot--active', index === visibleIndex);
                });
              }
            }
          });
        }, {
          root: this.elements.viewer,
          threshold: 0.5
        });

        // Observe all visible slides
        const visibleSlides = this.elements.viewer.querySelectorAll('.product__media-item:not([style*="display: none"])');
        visibleSlides.forEach(slide => {
          observer.observe(slide);
        });
      }

      handleScrollEnd() {
        if (window.innerWidth >= 750) return;

        const container = this.elements.viewer;
        const items = container.querySelectorAll('.product__media-item:not([style*="display: none"])');
        
        let closestItem = null;
        let closestDistance = Infinity;
        
        items.forEach(item => {
          const distance = Math.abs(item.getBoundingClientRect().left - container.getBoundingClientRect().left);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestItem = item;
          }
        });

        if (closestItem) {
          container.scrollTo({
            left: closestItem.offsetLeft,
            behavior: 'smooth'
          });
        }
      }

      debounceScrollEnd() {
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => this.handleScrollEnd(), 50);
      }

      alignToLeftMargin() {
        if (window.innerWidth >= 750) return;

        const container = this.elements.viewer;
        const items = Array.from(container.querySelectorAll('.product__media-item:not([style*="display: none"])'));
        
        if (!items.length) return;

        const containerLeft = container.getBoundingClientRect().left;
        let closestItem = items[0];
        let minDistance = Math.abs(closestItem.getBoundingClientRect().left - containerLeft);

        items.forEach(item => {
          const distance = Math.abs(item.getBoundingClientRect().left - containerLeft);
          if (distance < minDistance) {
            minDistance = distance;
            closestItem = item;
          }
        });

        if (closestItem) {
          container.scrollTo({
            left: closestItem.offsetLeft,
            behavior: 'smooth'
          });
        }
      }

      updateActiveDot(index) {
        if (!this.elements.dotsContainer) return;
        
        const dots = this.elements.dotsContainer.querySelectorAll('.slider-dot');
        dots.forEach((dot, i) => {
          dot.classList.toggle('slider-dot--active', i === index);
        });
        this.currentDotIndex = index;
      }
    }
  );
}